import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { EXPECTED_API_VERSION } from "../../config";
import { Bono } from "../../models/Bono";
import { Center } from "../../models/Center";
import { Category } from "../../models/Category";
import { Offer } from "../../models/Offer";
import { Banner } from "../../models/Banner";
import { Service } from "../../models/Service";
import { Schedules } from "../../models/Schedule";
import { wp_center, wp_provider } from "../../data_from_wp";
import { wp_assoc } from "./../../data_from_wp";
import { ProfileResponse } from "../../models/Profile";

export const api = createApi({
  reducerPath: "reformsApi",

  baseQuery: fetchBaseQuery({
    // baseUrl: `http://iconecta.info:8095/${EXPECTED_API_VERSION}/providers/${wp_provider}/`,
    baseUrl: `https://iconecta.online/${EXPECTED_API_VERSION}/providers/${wp_provider}/`,
    prepareHeaders: (headers) => {
      const token = localStorage.firebase_token || "";
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
        headers.set("Customer", `${wp_center}`);
      }
      return headers;
    },
  }),

  tagTypes: ["Profile"],

  endpoints: (builder) => ({
    getBanners: builder.query<Banner, void>({
      query: () => "banners",
    }),

    getServices: builder.query<
      { data: Service[] },
      { limit: number; start: number }
    >({
      query: ({ limit, start }) =>
        `centers/${wp_center}/services?limit=${limit}&start=${start}`,
      transformResponse: (response: { data: Service[] }) => {
        const final_response = response.data.map((service) => {
          return { ...service, wp_category: wp_assoc[service.id] };
        });
        return { data: final_response } as { data: Service[] };
      },
    }),

    getOffers: builder.query<Offer, { limit: number; start: number }>({
      query: ({ limit, start }) =>
        `centers/${wp_center}/promos?limit=${limit}&start=${start}`,
    }),

    getCategories: builder.query<Category, void>({
      query: () => `centers/${wp_center}/categories`,
    }),

    getCategoryService: builder.query<
      Service,
      { id: number; limit: number; start: number }
    >({
      query: ({ id, limit, start }) =>
        `centers/${wp_center}/services?cat=${id}&limit=${limit}&start=${start}`,
    }),

    getBonos: builder.query<Bono, { limit: number; start: number }>({
      query: ({ limit, start }) =>
        `centers/${wp_center}/vouchers?limit=${limit}&start=${start}`,
    }),

    getBonosPurchased: builder.query<Bono, { limit: number; start: number }>({
      query: ({ limit, start }) =>
        `centers/${wp_center}/me/vouchers?limit=${limit}&start=${start}`,
    }),

    getDataCenter: builder.query<Center, void>({
      query: () => `centers/${wp_center}`,
    }),

    getScheduleCategories: builder.query<
      { data: Schedules[] | null },
      { servicesId: number[]; date: string }
    >({
      query: ({ servicesId, date }) =>
        `centers/${wp_center}/employees/-1/schedules?date=${date}&services=${servicesId.join(
          ","
        )}`,
    }),
    postAppointment: builder.mutation<{ data: any }, { body: any }>({
      query: ({ body }) => ({
        url: `/centers/${wp_center}/me/appointments`,
        method: "POST",
        body,
      }),
    }),
    postNotification: builder.mutation<
      { data: any; token: string },
      { oneSignalObj: any }
    >({
      query: ({ oneSignalObj }) => ({
        url: `centers/${wp_center}/notifications`,
        method: "POST",
        body: oneSignalObj,
      }),
    }),
    deleteNotification: builder.mutation<
      { data: any; token: string },
      { notificationId: string }
    >({
      query: ({ notificationId }) => ({
        url: `notifications/${notificationId}`,
        method: "DELETE",
      }),
    }),
    getNotifications: builder.query<
      { data: { user: string; pass: string }; token: string },
      void
    >({
      query: () => ({
        url: "notifications",
        method: "GET",
      }),
    }),
    getProfile: builder.query<{ data: ProfileResponse; token: string }, void>({
      query: () => ({
        url: `centers/${wp_center}/me`,
        method: "GET",
      }),
      providesTags: ["Profile"],
    }),
    patchProfile: builder.mutation<
      { data: ProfileResponse; token: string },
      any
    >({
      query: (body) => ({
        url: `centers/${wp_center}/me`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),
  }),
});

export const {
  useGetBannersQuery,

  useGetServicesQuery,

  useGetOffersQuery,

  useGetCategoriesQuery,

  useGetCategoryServiceQuery,

  useGetBonosQuery,

  useGetBonosPurchasedQuery,

  useGetDataCenterQuery,

  useGetScheduleCategoriesQuery,

  usePostAppointmentMutation,

  useGetNotificationsQuery,

  useDeleteNotificationMutation,

  usePostNotificationMutation,

  useGetProfileQuery,
  usePatchProfileMutation,
} = api;
